export type EbvPercentageBandRow = {
  avg_type: string;
  order?: number;
  [key: string]: string | number | undefined;
};

/**
 * i4 CalculateTopPercent parity: minimum qualifying percentile rank for an EBV value
 * against B% or R% band rows (setting 29).
 */
function calculateTopPercentRank(
  ebvValue: number,
  ebvNumber: number,
  bands: EbvPercentageBandRow[],
  bandPrefix: 'B' | 'R',
  sortMode: string,
): number | null {
  if (!Number.isFinite(ebvValue)) return null;

  const traitBands = bands.filter(
    (row) =>
      typeof row.avg_type === 'string' &&
      row.avg_type.length >= 4 &&
      row.avg_type.startsWith(bandPrefix),
  );
  if (traitBands.length === 0) return null;

  const avgKey = `ebv_${ebvNumber}_avg`;
  const sortChar = (
    sortMode.padEnd(26, 'A')[ebvNumber - 1] ?? 'A'
  ).toUpperCase();
  const descending = sortChar === 'D';

  let minRank: number | null = null;
  for (const row of traitBands) {
    const raw = row[avgKey];
    if (raw === null || raw === undefined || raw === '' || raw === '-')
      continue;
    const threshold = parseFloat(String(raw));
    if (Number.isNaN(threshold)) continue;

    const qualifies = descending
      ? threshold <= ebvValue
      : threshold >= ebvValue;
    if (!qualifies) continue;

    const rank =
      typeof row.order === 'number'
        ? row.order
        : parseInt(String(row.avg_type).slice(1, 4), 10);
    if (Number.isNaN(rank)) continue;

    if (minRank === null || rank < minRank) minRank = rank;
  }

  if (minRank === null) {
    const thresholds = traitBands
      .map((row) => parseFloat(String(row[avgKey] ?? '')))
      .filter((n) => !Number.isNaN(n));
    if (thresholds.length === 0) return null;
    const minT = Math.min(...thresholds);
    const maxT = Math.max(...thresholds);
    if (descending) {
      if (ebvValue > maxT) return 1;
      if (ebvValue < minT) return 99;
    } else {
      if (ebvValue < minT) return 1;
      if (ebvValue > maxT) return 99;
    }
    return null;
  }

  if (minRank < 1) return 1;
  if (minRank > 99) return 99;
  return minRank;
}

/** CANG TopPercent band prefix: Black in ebvDescription → B%, else R% (i4 Contains('Black')). */
export function resolveBrBandPrefix(
  ebvDescription: string | null | undefined,
): 'B' | 'R' {
  return ebvDescription?.toLowerCase().includes('black') ? 'B' : 'R';
}

export type ResolveTraitPercentileOptions = {
  showTopPercentRow: boolean;
  brPercentileBands: EbvPercentageBandRow[];
  topPercentBandPrefix: 'B' | 'R';
  ebvSortMode: string;
};

/**
 * Percentile for tables/charts: band lookup for CANG TopPercent, else stored ebvNPercentile.
 */
export function resolveTraitPercentileNumber(
  raw: Record<string, unknown>,
  fieldIndex: number,
  options: ResolveTraitPercentileOptions,
): number | null {
  const valueKey = `ebv${fieldIndex}`;
  const pctKey = `ebv${fieldIndex}Percentile`;

  if (options.showTopPercentRow) {
    const rawValue = raw[valueKey];
    const numericValue =
      rawValue !== null && rawValue !== undefined && rawValue !== ''
        ? parseFloat(String(rawValue))
        : NaN;
    if (!Number.isNaN(numericValue)) {
      const calculated = calculateTopPercentRank(
        numericValue,
        fieldIndex,
        options.brPercentileBands,
        options.topPercentBandPrefix,
        options.ebvSortMode,
      );
      if (calculated !== null && calculated !== undefined) {
        return calculated;
      }
    }
  }

  const rawPct = raw[pctKey];
  if (rawPct === null || rawPct === undefined || rawPct === '') return null;
  const n = Number(rawPct);
  return Number.isNaN(n) ? null : n;
}
